"use client";

import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { submitContactForm } from "@/app/actions/contact-form";

const NEEDS_OPTIONS = [
  "Fine-tuning / custom model",
  "Hosted API / cloud",
  "On-prem / VPC deployment",
  "Support / SLA",
  "Not sure / Other",
] as const;

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactFormDialog({ open, onOpenChange }: ContactFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    needs: [] as string[],
    useCaseDetails: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);

      if (result.ok) {
        toast.success("Thank you! We'll be in touch soon.");
        onOpenChange(false);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          needs: [],
          useCaseDetails: "",
        });
      } else {
        toast.error(result.error || "Failed to submit form");
      }
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const formatUsPhoneNumber = (digits: string) => {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    const ext = digits.slice(10);

    if (digits.length === 0) {
      return "";
    }

    if (digits.length < 3) {
      return `(${area}`;
    }

    if (digits.length === 3) {
      return `(${area})`;
    }

    if (digits.length <= 6) {
      return `(${area}) ${prefix}`;
    }

    const base = `(${area}) ${prefix}-${line}`;
    return ext ? `${base} x${ext}` : base;
  };

  const formatPhoneNumber = (value: string) => {
    const trimmed = value.trim();
    const hasPlus = trimmed.startsWith("+");
    const digits = trimmed.replace(/\D/g, "");

    if (!digits) {
      return hasPlus ? "+" : "";
    }

    if (hasPlus) {
      if (digits.startsWith("1")) {
        return `+1 ${formatUsPhoneNumber(digits.slice(1))}`;
      }

      return `+${digits}`;
    }

    if (digits.startsWith("1") && digits.length > 10) {
      return `+1 ${formatUsPhoneNumber(digits.slice(1))}`;
    }

    if (digits.length <= 10) {
      return formatUsPhoneNumber(digits);
    }

    return digits;
  };

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phone: formatPhoneNumber(value) }));
  };

  const handleNeedsChange = (option: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      needs: checked
        ? [...prev.needs, option]
        : prev.needs.filter((n) => n !== option),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Nearly there!</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Just a few more details...
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                placeholder="First"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                placeholder="Last"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Company email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Your work email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone number (optional)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              autoComplete="tel"
              inputMode="tel"
              aria-describedby="phone-help"
            />
          </div>

          <div className="space-y-3">
            <Label>What do you need? (optional)</Label>
            <div className="space-y-2">
              {NEEDS_OPTIONS.map((option) => (
                <label
                  key={option}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <Checkbox
                    checked={formData.needs.includes(option)}
                    onCheckedChange={(checked) =>
                      handleNeedsChange(option, checked)
                    }
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="useCaseDetails">Use case details (optional)</Label>
            <Textarea
              id="useCaseDetails"
              placeholder="Tell us more about your use case..."
              value={formData.useCaseDetails}
              onChange={(e) => handleInputChange("useCaseDetails", e.target.value)}
              rows={4}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
